import { formatMoney, formatMoneyAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdInfoOutline } from 'react-icons/md';

import { Placement } from '@Components/designConstants';

import { CellData } from './active-ott-constants';

import './SpendBreakdown.scss';

interface SpendBreakdownProps {
  data: CellData;
}

export const SpendBreakdown = ({ data }: SpendBreakdownProps) => {
  // Show a full cost breakdown when any fees are included in the spend.
  const showBreakdown = (data.spend ?? 0) > 0 && (data.includeFees || data.includeCommission);

  const hasAdServingFee = (data.adServingFee ?? 0) > 0;
  const hasCommission = (data.commission ?? 0) > 0;
  const hasDspCpmFee = (data.dspCpmFee ?? 0) > 0;
  const hasDspCommissionFee = (data.dspCommissionFee ?? 0) > 0;

  // Subtract out the included fees/commission to get the media spend amount.
  const calculateMediaSpend = () => {
    let mediaSpend = data.spend ?? 0;
    if (data.includeFees) {
      mediaSpend -= (data.adServingFee ?? 0) + (data.dspCpmFee ?? 0);
    }
    if (data.includeCommission) {
      mediaSpend -= data.commission ?? 0;
    }
    if (hasDspCommissionFee) {
      mediaSpend -= data.dspCommissionFee ?? 0;
    }
    return mediaSpend;
  };
  return (
    <div className='active-ott-spend-breakdown'>
      <div className='spend-totals'>
        <div className='spend-breakdown-label' hidden={!showBreakdown}>
          <span>Media Spend:</span>
          <span className='spend-breakdown-amount'>{formatMoneyAsInt(calculateMediaSpend())}</span>
          {/* Show info icon next to media spend when there's a dsp commission-type fee to explain
              that the media spend being shown is lower than what was actually booked, since dsp
              commission is baked into the budget. */}
          <span className='dsp-info-icon' hidden={!data.includeFees || !hasDspCommissionFee}>
            <OverlayTrigger
              placement={Placement.RIGHT_START}
              overlay={
                <Tooltip className='dsp-info-tooltip'>
                  <p className='dsp-info-question'>
                    Why is the media spend less than what was booked?
                  </p>
                  <p>
                    When the DSP uses a commission-type fee, this fee is baked into media spend we
                    order, so the actual media value is less than the booked spend.
                  </p>
                </Tooltip>
              }
            >
              <MdInfoOutline />
            </OverlayTrigger>
          </span>
        </div>
        <div className='spend-breakdown-label' hidden={!data.includeFees || !hasAdServingFee}>
          <span>Ad Serving Fee:</span>
          <span className='spend-breakdown-amount'>{formatMoneyAsInt(data.adServingFee ?? 0)}</span>
        </div>
        <div className='spend-breakdown-label' hidden={!data.includeFees || !hasDspCpmFee}>
          <span>Dsp Fee:</span>
          <span className='spend-breakdown-amount'>{formatMoneyAsInt(data.dspCpmFee ?? 0)}</span>
        </div>
        <div className='spend-breakdown-label' hidden={!data.includeFees || !hasDspCommissionFee}>
          <span>Dsp Fee:</span>
          <span className='spend-breakdown-amount'>
            {formatMoneyAsInt(data.dspCommissionFee ?? 0)}
          </span>
        </div>
        <div className='spend-breakdown-label' hidden={!data.includeCommission || !hasCommission}>
          <span>Commission:</span>
          <span className='spend-breakdown-amount'>{formatMoneyAsInt(data.commission ?? 0)}</span>
        </div>
        <hr className='spend-breakdown-divider' hidden={!showBreakdown} />
        <div className='spend-breakdown-label'>
          <span>Spend:</span>
          <span className='spend-breakdown-amount'>{formatMoneyAsInt(data.spend ?? 0)}</span>
        </div>
      </div>
      <div className='cpm-spacer' hidden={!showBreakdown} />
      <div className='spend-breakdown-label'>
        <span>CPM:</span>
        <span className='spend-breakdown-amount'>{data.cpm ? formatMoney(data.cpm) : '$0'}</span>
      </div>
    </div>
  );
};
